import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api } from '../../utils/api';

const GOOGLE_FONTS = ['Inter', 'Roboto', 'Open Sans', 'Playfair Display', 'Lora', 'Montserrat', 'Oswald'];

export default function MenuManagement() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(null);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('categories');

  const fetchMenu = useCallback(async () => {
    const res = await api('/api/owners/me/menu', { getToken });
    if (!res.ok) {
      if (res.status === 403) {
        navigate('/', { replace: true });
        return;
      }
      setError('Failed to load menu');
      return;
    }
    const d = await res.json();
    setMenu(d);
    setError(null);
  }, [getToken, navigate]);

  const fetchBusiness = useCallback(async () => {
    const res = await api('/api/owners/me/business', { getToken });
    if (res.ok) setBusiness(await res.json());
  }, [getToken]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await Promise.all([fetchMenu(), fetchBusiness()]);
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [fetchMenu, fetchBusiness]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <p className="text-slate-600 dark:text-slate-400">Loading menu…</p>
      </div>
    );
  }

  if (error || !menu) {
    return (
      <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6">
        <p className="text-red-700 dark:text-red-300">{error || 'Failed to load menu'}</p>
        <Link to="/owner/dashboard" className="mt-4 inline-block text-amber-600 hover:text-amber-700">← Back to dashboard</Link>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Link to="/owner/dashboard" className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 mb-2 inline-block">
            ← Back to dashboard
          </Link>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
            Menu management
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Add categories and items for your menu.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setPreviewOpen(!previewOpen)}
          className="self-start px-4 py-2.5 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 text-sm font-medium transition-colors"
        >
          {previewOpen ? 'Hide preview' : 'Live preview'}
        </button>
      </div>

      {previewOpen && menu?.categories && (
        <MenuPreview categories={menu.categories} business={business} />
      )}

      <div
        role="tablist"
        aria-label="Menu management tabs"
        className="flex p-1 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
      >
        {[
          { id: 'categories', label: 'Categories' },
          { id: 'modifiers', label: 'Modifiers' },
        ].map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
                isActive
                  ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'categories' && (
        <div className="space-y-6">
          <AddCategorySection onAdded={fetchMenu} getToken={getToken} setError={setError} />
          {menu.categories?.length === 0 && (
            <p className="text-slate-500 dark:text-slate-400 py-6 text-center text-sm">
              Tap <span className="font-medium text-amber-600 dark:text-amber-500">+ Add category</span> above to create your first category.
            </p>
          )}
          {menu.categories?.map((cat) => (
            <CategorySection
              key={cat.id}
              category={cat}
              modifierGroups={menu.modifierGroups || []}
              onUpdate={fetchMenu}
              getToken={getToken}
              setError={setError}
            />
          ))}
        </div>
      )}

      {activeTab === 'modifiers' && (
        <div className="space-y-6">
          <AddModifierGroupSection onAdded={fetchMenu} getToken={getToken} setError={setError} />
          {(!menu.modifierGroups || menu.modifierGroups.length === 0) && (
            <p className="text-slate-500 dark:text-slate-400 py-6 text-center text-sm">
              Tap <span className="font-medium text-amber-600 dark:text-amber-500">+ Add modifier</span> above to create your first modifier group (e.g. Size, Sauce).
            </p>
          )}
          {menu.modifierGroups?.map((group) => (
            <ModifierGroupSection
              key={group.id}
              group={group}
              onUpdate={fetchMenu}
              getToken={getToken}
              setError={setError}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CategorySection({ category, modifierGroups = [], onUpdate, getToken, setError }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(category.name);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const serverItems = useMemo(() => category.items ?? [], [category.items]);
  const reorderSig = useMemo(
    () => serverItems.map((it) => `${it.id}:${it.sortOrder ?? 0}`).join('|'),
    [serverItems],
  );
  const [localItems, setLocalItems] = useState(serverItems);
  const localItemsRef = useRef(localItems);
  const syncRef = useRef(reorderSig);
  const [editingItemId, setEditingItemId] = useState(null);

  useEffect(() => {
    localItemsRef.current = localItems;
  }, [localItems]);

  useEffect(() => {
    if (reorderSig !== syncRef.current) {
      syncRef.current = reorderSig;
      const next = [...serverItems];
      setLocalItems(next);
      localItemsRef.current = next;
    }
  }, [category.id, reorderSig, serverItems]);

  // TouchSensor first: on phones, PointerSensor + distance often loses to browser scroll.
  // Short delay + tolerance distinguishes scroll from drag on the grip. Mouse uses PointerSensor.
  const sensors = useSensors(
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 12 },
    }),
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const saveItemOrder = useCallback(async (nextItems) => {
    if (nextItems.length === 0) return;
    setError(null);
    const prev = [...localItemsRef.current];
    setLocalItems(nextItems);
    localItemsRef.current = nextItems;
    try {
      const res = await api(`/api/owners/me/categories/${category.id}/items/order`, {
        getToken,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemIds: nextItems.map((i) => i.id) }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || `Error ${res.status}`);
      await onUpdate();
    } catch (err) {
      setLocalItems(prev);
      localItemsRef.current = prev;
      setError(err.message);
    }
  }, [category.id, getToken, onUpdate, setError]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const items = localItemsRef.current;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    saveItemOrder(arrayMove(items, oldIndex, newIndex));
  }, [saveItemOrder]);

  const handlePositionCommit = useCallback((itemId, rawValue) => {
    const items = localItemsRef.current;
    const n = items.length;
    if (n <= 1) return;
    let pos = parseInt(String(rawValue).trim(), 10);
    if (Number.isNaN(pos)) return;
    pos = Math.min(Math.max(1, pos), n);
    const oldIdx = items.findIndex((i) => i.id === itemId);
    if (oldIdx < 0) return;
    const newIdx = pos - 1;
    if (newIdx === oldIdx) return;
    saveItemOrder(arrayMove(items, oldIdx, newIdx));
  }, [saveItemOrder]);

  const handleItemEditingChange = useCallback((itemId, isEditing) => {
    setEditingItemId((prev) => {
      if (!isEditing) return prev === itemId ? null : prev;
      return itemId;
    });
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api(`/api/owners/me/categories/${category.id}`, {
        getToken,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `Error ${res.status}`);
      setEditing(false);
      await onUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete category "${category.name}" and all its items?`)) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api(`/api/owners/me/categories/${category.id}`, {
        getToken,
        method: 'DELETE',
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.error || `Error ${res.status}`);
      }
      await onUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
      <div
        className="flex items-center justify-between gap-4 p-4 cursor-pointer min-h-[56px]"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="text-slate-400 select-none">
            {expanded ? '▼' : '▶'}
          </span>
          {editing ? (
            <form
              onSubmit={handleSave}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 flex flex-col sm:flex-row gap-2 min-w-0"
            >
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full sm:flex-1 min-w-0 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900"
                placeholder="Category name"
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={saving || !name.trim()}
                  className="flex-1 sm:flex-none px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-medium min-h-[44px]"
                >
                  {saving ? '…' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => { setEditing(false); setName(category.name); }}
                  className="flex-1 sm:flex-none px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 min-h-[44px]"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 truncate">
              {category.name}
            </h2>
          )}
        </div>
        {!editing && (
          <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400"
              title="Edit category"
            >
              ✏️
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={saving}
              className="p-3 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 disabled:opacity-50"
              title="Delete category"
            >
              🗑️
            </button>
          </div>
        )}
      </div>

      {expanded && (
        <div className="border-t border-slate-200 dark:border-slate-700 p-4 space-y-4 bg-slate-50/50 dark:bg-slate-900/30">
          {localItems.length === 0 && !addItemOpen && (
            <p className="text-slate-500 dark:text-slate-400 text-sm py-2">
              No items. Add your first item.
            </p>
          )}
          {localItems.length > 0 && (
            <p className="text-xs text-slate-500 dark:text-slate-400 -mt-1 mb-2">
              Drag the grip to reorder items in this category, or set the # position. Customer menu uses this order.
              <span className="block sm:inline sm:before:content-[' '] text-slate-500 dark:text-slate-400 font-normal">
                On a phone, touch and hold the grip briefly, then drag.
              </span>
            </p>
          )}
          {localItems.length > 0 && (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={localItems.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-4">
                  {localItems.map((item, index) => (
                    <SortableMenuItemRow
                      key={item.id}
                      item={item}
                      categoryId={category.id}
                      modifierGroups={modifierGroups}
                      onUpdate={onUpdate}
                      getToken={getToken}
                      setError={setError}
                      positionIndex={index + 1}
                      onPositionCommit={handlePositionCommit}
                      onEditingChange={handleItemEditingChange}
                      sortDisabled={editingItemId === item.id}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
          {addItemOpen ? (
            <ItemForm
              categoryId={category.id}
              modifierGroups={modifierGroups}
              onSaved={() => { setAddItemOpen(false); onUpdate(); }}
              onCancel={() => setAddItemOpen(false)}
              getToken={getToken}
              setError={setError}
            />
          ) : (
            <button
              type="button"
              onClick={() => setAddItemOpen(true)}
              className="w-full py-4 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-amber-500 hover:bg-amber-50/50 dark:hover:bg-amber-900/10 text-slate-600 dark:text-slate-400 hover:text-amber-700 dark:hover:text-amber-400 font-medium min-h-[52px]"
            >
              + Add item
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function AddCategorySection({ onAdded, getToken, setError }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api('/api/owners/me/categories', {
        getToken,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `Error ${res.status}`);
      setName('');
      setOpen(false);
      await onAdded();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full py-5 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-amber-500 hover:bg-amber-50/50 dark:hover:bg-amber-900/10 text-slate-600 dark:text-slate-400 hover:text-amber-700 dark:hover:text-amber-400 font-medium min-h-[60px]"
      >
        + Add category
      </button>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 flex flex-col sm:flex-row gap-3"
    >
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Category name (e.g. Drinks, Entrees, Sides)"
        className="flex-1 px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 min-h-[48px]"
        autoFocus
      />
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving || !name.trim()}
          className="flex-1 sm:flex-none px-6 py-3 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-medium min-h-[48px]"
        >
          {saving ? 'Adding…' : 'Add'}
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setName(''); }}
          className="px-6 py-3 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 min-h-[48px]"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function ItemRow({
  item,
  categoryId,
  modifierGroups = [],
  onUpdate,
  getToken,
  setError,
  positionIndex,
  onPositionCommit,
  dragHandleProps,
  isDragging,
  onEditingChange,
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    onEditingChange?.(item.id, editing);
  }, [editing, item.id, onEditingChange]);

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api(`/api/owners/me/items/${item.id}`, {
        getToken,
        method: 'DELETE',
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.error || `Error ${res.status}`);
      }
      await onUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleDisabled = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await api(`/api/owners/me/items/${item.id}`, {
        getToken,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isDisabled: !item.isDisabled }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.error || `Error ${res.status}`);
      }
      await onUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <ItemForm
        categoryId={categoryId}
        item={item}
        modifierGroups={modifierGroups}
        onSaved={() => { setEditing(false); onUpdate(); }}
        onCancel={() => setEditing(false)}
        getToken={getToken}
        setError={setError}
      />
    );
  }

  const baseUrl = process.env.REACT_APP_API_URL || '';
  const imageSrc = item.imageUrl?.startsWith('/')
    ? (baseUrl ? `${baseUrl.replace(/\/$/, '')}${item.imageUrl}` : item.imageUrl)
    : item.imageUrl;

  return (
    <div
      className={`relative flex gap-3 sm:gap-4 p-4 rounded-xl border bg-white dark:bg-slate-800 items-center ${
        item.isDisabled ? 'opacity-60 border-amber-300 dark:border-amber-800' : 'border-slate-200 dark:border-slate-700'
      } ${isDragging ? 'shadow-lg ring-2 ring-amber-400/50' : ''}`}
    >
      {dragHandleProps && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Drag to reorder. On touch screens, press and hold briefly then drag."
          className="flex-shrink-0 p-2 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-grab active:cursor-grabbing touch-none select-none [-webkit-touch-callout:none] min-h-[44px] min-w-[44px] flex items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
          {...dragHandleProps}
        >
          ⋮⋮
        </div>
      )}
      {onPositionCommit != null && positionIndex != null && (
        <label className="flex flex-col items-center gap-0.5 flex-shrink-0">
          <span className="text-[10px] uppercase tracking-wide text-slate-400">#</span>
          <input
            type="number"
            min={1}
            max={999}
            defaultValue={positionIndex}
            key={`${item.id}-pos-${positionIndex}`}
            onBlur={(e) => onPositionCommit(item.id, e.target.value)}
            className="w-11 px-1 py-1.5 text-center text-sm rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900"
            aria-label="Position in category"
          />
        </label>
      )}
      {item.isDisabled && (
        <span className="absolute top-2 right-14 px-2 py-0.5 text-xs font-semibold rounded bg-amber-200 text-amber-900 dark:bg-amber-800 dark:text-amber-100">
          Disabled
        </span>
      )}
      {imageSrc && (
        <img
          src={imageSrc}
          alt=""
          className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
        />
      )}
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-900 dark:text-slate-100">{item.name}</div>
        {item.description && (
          <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 mt-0.5">
            {item.description}
          </p>
        )}
        <div className="flex flex-wrap gap-2 mt-2">
          <span className="text-amber-600 dark:text-amber-500 font-medium">
            ${Number(item.price).toFixed(2)}
          </span>
          {item.dailyQuantityLimit != null && (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Limit {item.dailyQuantityLimit}/day
            </span>
          )}
          {item.addons?.length > 0 && (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              +{item.addons.length} add-on{item.addons.length !== 1 ? 's' : ''}
            </span>
          )}
          {item.modifierGroupIds?.length > 0 && (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {item.modifierGroupIds.length} modifier group{item.modifierGroupIds.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <button
          type="button"
          onClick={handleToggleDisabled}
          disabled={saving}
          className={`p-3 rounded-lg min-h-[44px] min-w-[44px] ${item.isDisabled ? 'hover:bg-emerald-100 dark:hover:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' : 'hover:bg-amber-100 dark:hover:bg-amber-900/30 text-amber-600 dark:text-amber-400'}`}
          title={item.isDisabled ? 'Enable — show on menu' : 'Disable — hide from menu immediately'}
        >
          {item.isDisabled ? '👁️' : '🚫'}
        </button>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 min-h-[44px] min-w-[44px]"
          title="Edit"
        >
          ✏️
        </button>
        <button
          type="button"
          onClick={handleDelete}
          disabled={saving}
          className="p-3 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 disabled:opacity-50 min-h-[44px] min-w-[44px]"
          title="Delete"
        >
          🗑️
        </button>
      </div>
    </div>
  );
}

function SortableMenuItemRow({
  item,
  categoryId,
  modifierGroups,
  onUpdate,
  getToken,
  setError,
  positionIndex,
  onPositionCommit,
  onEditingChange,
  sortDisabled,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: sortDisabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : undefined,
  };

  const dragHandleProps = sortDisabled ? undefined : { ...attributes, ...listeners };

  return (
    <div ref={setNodeRef} style={style}>
      <ItemRow
        item={item}
        categoryId={categoryId}
        modifierGroups={modifierGroups}
        onUpdate={onUpdate}
        getToken={getToken}
        setError={setError}
        positionIndex={positionIndex}
        onPositionCommit={onPositionCommit}
        dragHandleProps={dragHandleProps}
        isDragging={isDragging}
        onEditingChange={onEditingChange}
      />
    </div>
  );
}

function MenuPreview({ categories, business }) {
  const [selectedCatId, setSelectedCatId] = useState(null);
  const baseUrl = process.env.REACT_APP_API_URL || '';
  const resolveImg = (url) =>
    url?.startsWith('/') && baseUrl ? `${baseUrl.replace(/\/$/, '')}${url}` : url;

  const pos = business?.menuImagePosition || 'top';

  const activeCat = useMemo(() => {
    if (!categories?.length) return null;
    return categories.find((c) => c.id === selectedCatId) || categories[0];
  }, [categories, selectedCatId]);

  useEffect(() => {
    if (categories?.length && !selectedCatId) setSelectedCatId(categories[0].id);
  }, [categories, selectedCatId]);

  const bgUrl = business?.menuBackgroundImageUrl
    ? resolveImg(business.menuBackgroundImageUrl)
    : null;

  const fontName = business?.menuFont?.split(',')[0]?.replace(/"/g, '').trim();
  const needsGoogleFont = fontName && GOOGLE_FONTS.includes(fontName);

  const menuStyle = {
    backgroundColor: business?.menuBackgroundColor || '#faf9f7',
    fontFamily: business?.menuFont || undefined,
    color: business?.menuTextColor || '#1a1a1a',
  };

  const isVertical = pos === 'top' || pos === 'bottom';

  return (
    <div className="rounded-xl border-2 border-slate-200 dark:border-slate-700 overflow-hidden">
      <div className="bg-slate-100 dark:bg-slate-800 px-4 py-2 border-b border-slate-200 dark:border-slate-700">
        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
          Customer menu preview
        </p>
      </div>
      <div className="relative max-h-[500px] overflow-y-auto" style={menuStyle}>
        {bgUrl && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ backgroundImage: `url(${bgUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
          />
        )}
        {needsGoogleFont && (
          <link
            href={`https://fonts.googleapis.com/css2?family=${fontName.replace(/\s+/g, '+')}&display=swap`}
            rel="stylesheet"
          />
        )}
        <div className="relative z-10 p-4 space-y-4">
          {categories?.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
              {categories.map((cat) => {
                const isActive = activeCat?.id === cat.id;
                const tileBg = business?.menuCategoryTileColor || '#c9a227';
                const tileText = business?.menuCategoryTextColor || '#1a1a1a';
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setSelectedCatId(cat.id)}
                    className="flex-shrink-0 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap border transition-colors"
                    style={isActive
                      ? { backgroundColor: tileBg, color: tileText, borderColor: tileBg }
                      : { backgroundColor: 'rgba(255,255,255,0.8)', color: business?.menuTextColor || '#1a1a1a', borderColor: 'rgba(0,0,0,0.08)' }
                    }
                  >
                    {cat.name}
                  </button>
                );
              })}
            </div>
          )}
          {activeCat?.items?.map((item) => {
            const imgSrc = resolveImg(item.imageUrl);
            const imgBlock = imgSrc ? (
              <div className={isVertical ? 'w-full h-32' : 'flex-shrink-0 w-20 h-20'}>
                <img src={imgSrc} alt="" className="w-full h-full object-cover" style={{ borderRadius: isVertical ? 0 : 8 }} />
              </div>
            ) : null;

            const content = (
              <div className={isVertical ? 'p-3' : 'flex-1 min-w-0 py-1'}>
                <p className="font-semibold text-sm">{item.name}</p>
                {item.description && <p className="text-xs opacity-70 line-clamp-1 mt-0.5">{item.description}</p>}
                <p className="text-sm font-semibold mt-1">${Number(item.price).toFixed(2)}</p>
              </div>
            );

            if (pos === 'left' || pos === 'right') {
              return (
                <div key={item.id} className="bg-white/90 rounded-lg overflow-hidden flex gap-3 p-2" style={{ border: '1px solid rgba(0,0,0,0.06)' }}>
                  {pos === 'left' && imgBlock}
                  {content}
                  {pos === 'right' && imgBlock}
                </div>
              );
            }
            return (
              <div key={item.id} className="bg-white/90 rounded-lg overflow-hidden" style={{ border: '1px solid rgba(0,0,0,0.06)' }}>
                {pos === 'top' && imgBlock}
                {content}
                {pos === 'bottom' && imgBlock}
              </div>
            );
          })}
          {(!activeCat?.items || activeCat.items.length === 0) && (
            <p className="text-sm opacity-60 text-center py-6">No items in this category</p>
          )}
        </div>
      </div>
    </div>
  );
}

function ItemForm({ categoryId, item, modifierGroups = [], onSaved, onCancel, getToken, setError }) {
  const isEdit = !!item;
  const [name, setName] = useState(item?.name || '');
  const [description, setDescription] = useState(item?.description || '');
  const [price, setPrice] = useState(item ? String(item.price) : '');
  const [imageUrl, setImageUrl] = useState(item?.imageUrl || '');
  const [pointsPerPurchase, setPointsPerPurchase] = useState(item?.pointsPerPurchase != null ? String(item.pointsPerPurchase) : '');
  const [dailyQuantityLimit, setDailyQuantityLimit] = useState(item?.dailyQuantityLimit != null ? String(item.dailyQuantityLimit) : '');
  const [isDisabled, setIsDisabled] = useState(item?.isDisabled ?? false);
  const [addons, setAddons] = useState(
    item?.addons?.map((a) => ({ name: a.name, price: String(a.price) })) || []
  );
  const [selectedGroupIds, setSelectedGroupIds] = useState(
    Array.isArray(item?.modifierGroupIds) ? [...item.modifierGroupIds] : []
  );
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const toggleGroup = (groupId) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId) ? prev.filter((g) => g !== groupId) : [...prev, groupId]
    );
  };

  const handleAddAddon = () => {
    setAddons([...addons, { name: '', price: '' }]);
  };

  const handleAddonChange = (i, field, value) => {
    const next = [...addons];
    next[i] = { ...next[i], [field]: value };
    setAddons(next);
  };

  const removeAddon = (i) => {
    setAddons(addons.filter((_, idx) => idx !== i));
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api('/api/owners/me/upload-image', {
        getToken,
        method: 'POST',
        body: formData,
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `Error ${res.status}`);
      if (d.url) setImageUrl(d.url);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const priceNum = parseFloat(price);
    if (!name.trim() || isNaN(priceNum) || priceNum < 0) return;

    const addonDtos = addons
      .filter((a) => a.name?.trim())
      .map((a) => ({ name: a.name.trim(), price: parseFloat(a.price) || 0 }));

    setSaving(true);
    setError(null);
    try {
      const url = isEdit
        ? `/api/owners/me/items/${item.id}`
        : '/api/owners/me/items';
      const pts = pointsPerPurchase.trim() === '' ? null : parseInt(pointsPerPurchase, 10);
      const dailyLimit = dailyQuantityLimit.trim() === '' ? null : parseInt(dailyQuantityLimit, 10);
      const body = isEdit
        ? { name: name.trim(), description: description.trim() || null, price: priceNum, imageUrl: imageUrl || null, pointsPerPurchase: (pts != null && !isNaN(pts)) ? pts : null, dailyQuantityLimit: (dailyLimit != null && !isNaN(dailyLimit) && dailyLimit > 0) ? dailyLimit : null, isDisabled, addons: addonDtos, modifierGroupIds: selectedGroupIds }
        : { categoryId, name: name.trim(), description: description.trim() || null, price: priceNum, imageUrl: imageUrl || null, pointsPerPurchase: (pts != null && !isNaN(pts)) ? pts : null, dailyQuantityLimit: (dailyLimit != null && !isNaN(dailyLimit) && dailyLimit > 0) ? dailyLimit : null, isDisabled, addons: addonDtos, modifierGroupIds: selectedGroupIds };

      const res = await api(url, {
        getToken,
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `Error ${res.status}`);
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const baseUrl = process.env.REACT_APP_API_URL || '';
  const imageSrc = imageUrl?.startsWith('/')
    ? (baseUrl ? `${baseUrl.replace(/\/$/, '')}${imageUrl}` : imageUrl)
    : imageUrl;

  return (
    <form onSubmit={handleSubmit} className="p-4 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10 space-y-4">
      <h3 className="font-semibold text-slate-900 dark:text-slate-100">
        {isEdit ? 'Edit item' : 'New item'}
      </h3>
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Name *
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className="w-full px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 min-h-[48px]"
          placeholder="e.g. Tacos"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          className="w-full px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900"
          placeholder="Optional description"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Price *
        </label>
        <input
          type="number"
          step="0.01"
          min="0"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          required
          className="w-full px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 min-h-[48px]"
          placeholder="0.00"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Points when purchased
        </label>
        <input
          type="number"
          min="0"
          value={pointsPerPurchase}
          onChange={(e) => setPointsPerPurchase(e.target.value)}
          className="w-full px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 min-h-[48px]"
          placeholder="Leave blank for default"
        />
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Optional. Uses business default if blank.</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Daily quantity limit
        </label>
        <input
          type="number"
          min="0"
          placeholder="Unlimited"
          value={dailyQuantityLimit}
          onChange={(e) => setDailyQuantityLimit(e.target.value)}
          className="w-full px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 min-h-[48px]"
        />
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Max items sold per day. Leave blank for unlimited.</p>
      </div>
      <div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={isDisabled}
            onChange={(e) => setIsDisabled(e.target.checked)}
            className="rounded border-slate-300"
          />
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Disabled — hide from menu and block purchases</span>
        </label>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Image
        </label>
        <div className="flex gap-3 items-start">
          {imageSrc && (
            <img
              src={imageSrc}
              alt=""
              className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
            />
          )}
          <div className="flex-1">
            <label className="inline-block px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer min-h-[48px]">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                disabled={uploading}
                className="hidden"
              />
              {uploading ? 'Uploading…' : imageSrc ? 'Change image' : 'Upload image'}
            </label>
          </div>
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Add-ons
          </label>
          <button
            type="button"
            onClick={handleAddAddon}
            className="text-sm text-amber-600 hover:text-amber-700 dark:text-amber-500"
          >
            + Add add-on
          </button>
        </div>
        {addons.map((a, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <input
              type="text"
              value={a.name}
              onChange={(e) => handleAddonChange(i, 'name', e.target.value)}
              placeholder="Add-on name"
              className="flex-1 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 min-h-[44px]"
            />
            <input
              type="number"
              step="0.01"
              min="0"
              value={a.price}
              onChange={(e) => handleAddonChange(i, 'price', e.target.value)}
              placeholder="0.00"
              className="w-24 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 min-h-[44px]"
            />
            <button
              type="button"
              onClick={() => removeAddon(i)}
              className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 min-h-[44px] min-w-[44px]"
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Modifier groups
        </label>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
          Attach modifier groups customers must pick one option from (e.g. Size, Sauce). Selection won't change the price.
        </p>
        {modifierGroups.length === 0 ? (
          <p className="text-xs text-slate-500 dark:text-slate-400 py-2">
            No modifier groups yet. Create one in the <span className="font-medium text-amber-600 dark:text-amber-500">Modifiers</span> tab.
          </p>
        ) : (
          <div className="space-y-2">
            {modifierGroups.map((g) => {
              const checked = selectedGroupIds.includes(g.id);
              return (
                <label
                  key={g.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer min-h-[44px] ${
                    checked
                      ? 'border-amber-400 bg-amber-100/40 dark:bg-amber-900/20'
                      : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleGroup(g.id)}
                    className="rounded border-slate-300"
                  />
                  <span className="flex-1 min-w-0">
                    <span className="font-medium text-slate-900 dark:text-slate-100">{g.name}</span>
                    {g.options?.length > 0 && (
                      <span className="text-xs text-slate-500 dark:text-slate-400 ml-2">
                        {g.options.length} option{g.options.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>
      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          disabled={saving || !name.trim() || isNaN(parseFloat(price))}
          className="flex-1 py-3 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-medium min-h-[52px]"
        >
          {saving ? 'Saving…' : isEdit ? 'Save' : 'Add item'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-3 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 min-h-[52px]"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function AddModifierGroupSection({ onAdded, getToken, setError }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api('/api/owners/me/modifier-groups', {
        getToken,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `Error ${res.status}`);
      setName('');
      setOpen(false);
      await onAdded();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full py-5 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-amber-500 hover:bg-amber-50/50 dark:hover:bg-amber-900/10 text-slate-600 dark:text-slate-400 hover:text-amber-700 dark:hover:text-amber-400 font-medium min-h-[60px]"
      >
        + Add modifier
      </button>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 flex flex-col sm:flex-row gap-3"
    >
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Modifier name (e.g. Size, Sauce, Bread type)"
        className="flex-1 px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 min-h-[48px]"
        autoFocus
      />
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving || !name.trim()}
          className="flex-1 sm:flex-none px-6 py-3 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-medium min-h-[48px]"
        >
          {saving ? 'Adding…' : 'Add'}
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setName(''); }}
          className="px-6 py-3 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 min-h-[48px]"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function ModifierGroupSection({ group, onUpdate, getToken, setError }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(group.name);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [addOptionOpen, setAddOptionOpen] = useState(false);
  const addFormRef = useRef(null);

  useEffect(() => {
    if (addOptionOpen && addFormRef.current) {
      addFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [addOptionOpen]);

  const handleSaveName = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api(`/api/owners/me/modifier-groups/${group.id}`, {
        getToken,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `Error ${res.status}`);
      setEditing(false);
      await onUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete modifier "${group.name}" and all its options? Items using it will lose this group.`)) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api(`/api/owners/me/modifier-groups/${group.id}`, {
        getToken,
        method: 'DELETE',
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.error || `Error ${res.status}`);
      }
      await onUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveOptions = async (newOptions) => {
    setError(null);
    try {
      const res = await api(`/api/owners/me/modifier-groups/${group.id}`, {
        getToken,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ options: newOptions }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `Error ${res.status}`);
      await onUpdate();
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  return (
    <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
      <div
        className="flex items-center justify-between gap-4 p-4 cursor-pointer min-h-[56px]"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="text-slate-400 select-none">{expanded ? '▼' : '▶'}</span>
          {editing ? (
            <form
              onSubmit={handleSaveName}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 flex flex-col sm:flex-row gap-2 min-w-0"
            >
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full sm:flex-1 min-w-0 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900"
                placeholder="Modifier name"
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={saving || !name.trim()}
                  className="flex-1 sm:flex-none px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-medium min-h-[44px]"
                >
                  {saving ? '…' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => { setEditing(false); setName(group.name); }}
                  className="flex-1 sm:flex-none px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 min-h-[44px]"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 truncate">
                {group.name}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {group.options?.length || 0} option{(group.options?.length || 0) !== 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>
        {!editing && (
          <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400"
              title="Edit modifier name"
            >
              ✏️
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={saving}
              className="p-3 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 disabled:opacity-50"
              title="Delete modifier"
            >
              🗑️
            </button>
          </div>
        )}
      </div>

      {expanded && (
        <div className="border-t border-slate-200 dark:border-slate-700 p-4 space-y-4 bg-slate-50/50 dark:bg-slate-900/30">
          {addOptionOpen ? (
            <div ref={addFormRef}>
              <ModifierOptionForm
                allOptions={group.options || []}
                onSaved={async (updatedOptions) => {
                  await saveOptions(updatedOptions);
                  setAddOptionOpen(false);
                }}
                onCancel={() => setAddOptionOpen(false)}
                setError={setError}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAddOptionOpen(true)}
              className="w-full py-4 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-amber-500 hover:bg-amber-50/50 dark:hover:bg-amber-900/10 text-slate-600 dark:text-slate-400 hover:text-amber-700 dark:hover:text-amber-400 font-medium min-h-[52px]"
            >
              + Add option
            </button>
          )}
          {(group.options?.length || 0) === 0 && !addOptionOpen && (
            <p className="text-slate-500 dark:text-slate-400 text-sm py-2 text-center">
              Tap <span className="font-medium text-amber-600 dark:text-amber-500">+ Add option</span> above to create your first option.
            </p>
          )}
          {group.options?.map((option) => (
            <ModifierOptionRow
              key={option.id}
              option={option}
              allOptions={group.options}
              onSave={saveOptions}
              setError={setError}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function ModifierOptionRow({ option, allOptions, onSave, setError }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const editFormRef = useRef(null);

  useEffect(() => {
    if (editing && editFormRef.current) {
      editFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [editing]);

  const handleDelete = async () => {
    if (!window.confirm(`Delete option "${option.name}"?`)) return;
    setSaving(true);
    try {
      const remaining = (allOptions || [])
        .filter((o) => o.id !== option.id)
        .map((o) => ({ id: o.id, name: o.name, price: Number(o.price) || 0, imageUrl: o.imageUrl || null, sortOrder: o.sortOrder ?? 0 }));
      await onSave(remaining);
    } catch {
      // onSave surfaces the error
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div ref={editFormRef}>
        <ModifierOptionForm
          option={option}
          allOptions={allOptions || []}
          onSaved={async (updatedOptions) => {
            await onSave(updatedOptions);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
          setError={setError}
        />
      </div>
    );
  }

  return (
    <div className="relative flex gap-4 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-900 dark:text-slate-100">{option.name}</div>
      </div>
      <div className="flex flex-col gap-1">
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 min-h-[44px] min-w-[44px]"
          title="Edit"
        >
          ✏️
        </button>
        <button
          type="button"
          onClick={handleDelete}
          disabled={saving}
          className="p-3 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 disabled:opacity-50 min-h-[44px] min-w-[44px]"
          title="Delete"
        >
          🗑️
        </button>
      </div>
    </div>
  );
}

function ModifierOptionForm({ option, allOptions, onSaved, onCancel, setError }) {
  const isEdit = !!option;
  const [name, setName] = useState(option?.name || '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    setSaving(true);
    setError(null);
    try {
      const base = (allOptions || []).map((o) => ({
        id: o.id,
        name: o.name,
        price: Number(o.price) || 0,
        imageUrl: o.imageUrl || null,
        sortOrder: o.sortOrder ?? 0,
      }));

      let next;
      if (isEdit) {
        next = base.map((o) => (o.id === option.id
          ? { ...o, name: name.trim() }
          : o));
      } else {
        next = [
          ...base,
          {
            name: name.trim(),
            price: 0,
            imageUrl: null,
            sortOrder: base.length,
          },
        ];
      }
      await onSaved(next);
    } catch {
      // parent surfaces the error
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10 space-y-4">
      <h3 className="font-semibold text-slate-900 dark:text-slate-100">
        {isEdit ? 'Edit option' : 'New option'}
      </h3>
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Name *
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className="w-full px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 min-h-[48px]"
          placeholder="e.g. Small, Ranch, White bread"
        />
      </div>
      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          disabled={saving || !name.trim()}
          className="flex-1 py-3 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-medium min-h-[52px]"
        >
          {saving ? 'Saving…' : isEdit ? 'Save' : 'Add option'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-3 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 min-h-[52px]"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
